import React, { useCallback, useRef, useState } from 'react';
import styled, { DefaultTheme, withTheme, keyframes } from 'styled-components';
import FocusLock from 'react-focus-lock';
import ModalContainer from '../../atoms/Modal/ModalContainer';
import FooterButtonContainer from '../../atoms/Modal/FooterButtonContainer';
import Button from '../../atoms/Button/Button';
import Spinner from '../../atoms/Spinner/Spinner';
import { PaylinkFormRow } from '../../../types/paylink/PaylinkFormRow';
import { ParsedCustomerInfo } from '../../../types/paylink/ParsedCustomerInfo';
import { parseContentForPaylink } from '../../../services/aiParseService';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

interface MagicPaylinkModalProps {
    theme: DefaultTheme;
    onImport: (rows: PaylinkFormRow[], customer?: ParsedCustomerInfo) => void;
    hide: () => void;
}

type ModalState = 'idle' | 'hasContent' | 'processing' | 'done' | 'error';

interface CapturedContent {
    text: string;
    files: File[];
    previews: { name: string; type: 'image' | 'pdf' | 'text'; url?: string }[];
}

const MagicPaylinkModal: React.FC<MagicPaylinkModalProps> = ({ theme, onImport, hide }) => {
    const [state, setState] = useState<ModalState>('idle');
    const [content, setContent] = useState<CapturedContent>({ text: '', files: [], previews: [] });
    const [errorMessage, setErrorMessage] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [fileSizeWarning, setFileSizeWarning] = useState('');
    const [parsedRows, setParsedRows] = useState<PaylinkFormRow[]>([]);
    const [parsedCustomer, setParsedCustomer] = useState<ParsedCustomerInfo | undefined>();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const updateContent = useCallback((update: Partial<CapturedContent>) => {
        setContent(prev => {
            const next = { ...prev, ...update };
            if (update.previews) {
                next.previews = [...prev.previews, ...update.previews];
            }
            if (update.files) {
                next.files = [...prev.files, ...update.files];
            }
            return next;
        });
        setState('hasContent');
    }, []);

    const checkTotalFileSize = useCallback(
        (newFiles: File[]) => {
            const existingSize = content.files.reduce((sum, f) => sum + f.size, 0);
            const newSize = newFiles.reduce((sum, f) => sum + f.size, 0);
            const exceeds = existingSize + newSize > MAX_FILE_SIZE;
            setFileSizeWarning(exceeds ? 'Total file size exceeds the 10 MB limit.' : '');
            return exceeds;
        },
        [content.files]
    );

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            const { clipboardData } = e;

            // Check for pasted images
            const imageItems = Array.from(clipboardData.items).filter(item =>
                item.type.startsWith('image/')
            );

            if (imageItems.length > 0) {
                e.preventDefault();
                const newFiles: File[] = [];
                const newPreviews: CapturedContent['previews'] = [];

                for (const item of imageItems) {
                    const file = item.getAsFile();
                    if (file) {
                        newFiles.push(file);
                        newPreviews.push({
                            name: file.name || 'Pasted image',
                            type: 'image',
                            url: URL.createObjectURL(file),
                        });
                    }
                }

                if (newFiles.length > 0) {
                    checkTotalFileSize(newFiles);
                    updateContent({ files: newFiles, previews: newPreviews });
                }
                return;
            }
        },
        [updateContent, checkTotalFileSize]
    );

    const handleTextChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const text = e.target.value;
            setContent(prev => ({ ...prev, text }));
            setState(text.trim() || content.files.length > 0 ? 'hasContent' : 'idle');
        },
        [content.files.length]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            processFiles(Array.from(e.dataTransfer.files));
        },
        [updateContent]
    );

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files) {
                processFiles(Array.from(e.target.files));
            }
        },
        [updateContent]
    );

    const processFiles = (files: File[]) => {
        const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];
        const validFiles = files.filter(f => allowed.includes(f.type));
        if (validFiles.length === 0) return;

        checkTotalFileSize(validFiles);

        const newPreviews: CapturedContent['previews'] = validFiles.map(f => ({
            name: f.name,
            type: f.type === 'application/pdf' ? 'pdf' : 'image',
            url: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
        }));

        updateContent({ files: validFiles, previews: newPreviews });
    };

    const handleImport = async () => {
        setState('processing');
        setErrorMessage('');

        try {
            const result = await parseContentForPaylink(
                content.text || undefined,
                content.files.length > 0 ? content.files : undefined
            );

            if (result.rows.length === 0) {
                setState('error');
                setErrorMessage('No line items could be extracted from the provided content.');
                return;
            }

            setParsedRows(result.rows);
            setParsedCustomer(result.customer);
            setState('done');
        } catch (err) {
            setState('error');
            setErrorMessage(
                err instanceof Error ? err.message : 'Failed to process content. Please try again.'
            );
        }
    };

    const handleConfirmImport = () => {
        onImport(parsedRows, parsedCustomer);
        hide();
    };

    const handleClear = () => {
        content.previews.forEach(p => {
            if (p.url) URL.revokeObjectURL(p.url);
        });
        setContent({ text: '', files: [], previews: [] });
        setParsedRows([]);
        setParsedCustomer(undefined);
        setState('idle');
        setErrorMessage('');
        setFileSizeWarning('');
        if (textAreaRef.current) textAreaRef.current.value = '';
    };

    const hasContent = content.text.trim() !== '' || content.files.length > 0;

    return (
        <>
            <ModalContainer position="header">
                <AiHeaderTitle>
                    <SparkleIcon>✦</SparkleIcon>
                    AI Import
                </AiHeaderTitle>
            </ModalContainer>

            <ModalContainer position="content">
                {state === 'processing' && (
                    <SpinnerContainer>
                        <Spinner
                            text="Analyzing content..."
                            color={theme.colors.primary}
                            size={8}
                            loading
                        />
                    </SpinnerContainer>
                )}

                {state === 'done' && (
                    <ResultContainer>
                        <SuccessText>
                            Found {parsedRows.length} item{parsedRows.length !== 1 ? 's' : ''}
                            {parsedCustomer ? ' and customer information' : ''}. Please review the
                            extracted data before confirming. The information is AI-generated and{' '}
                            <u>may contain errors</u>.
                        </SuccessText>
                        {parsedCustomer && (
                            <CustomerPreview>
                                <CustomerPreviewTitle>Customer</CustomerPreviewTitle>
                                <CustomerGrid>
                                    {parsedCustomer.companyName && (
                                        <CustomerFieldGroup>
                                            <CustomerLabel>Company</CustomerLabel>
                                            <CustomerValue>
                                                {parsedCustomer.companyName}
                                            </CustomerValue>
                                        </CustomerFieldGroup>
                                    )}
                                    {parsedCustomer.organisationNumber && (
                                        <CustomerFieldGroup>
                                            <CustomerLabel>Org. number</CustomerLabel>
                                            <CustomerValue>
                                                {parsedCustomer.organisationNumber}
                                            </CustomerValue>
                                        </CustomerFieldGroup>
                                    )}
                                    {(parsedCustomer.firstName || parsedCustomer.lastName) && (
                                        <CustomerFieldGroup>
                                            <CustomerLabel>Name</CustomerLabel>
                                            <CustomerValue>
                                                {[parsedCustomer.firstName, parsedCustomer.lastName]
                                                    .filter(Boolean)
                                                    .join(' ')}
                                            </CustomerValue>
                                        </CustomerFieldGroup>
                                    )}
                                    {parsedCustomer.email && (
                                        <CustomerFieldGroup>
                                            <CustomerLabel>Email</CustomerLabel>
                                            <CustomerValue>{parsedCustomer.email}</CustomerValue>
                                        </CustomerFieldGroup>
                                    )}
                                    {parsedCustomer.phoneNumber && (
                                        <CustomerFieldGroup>
                                            <CustomerLabel>Phone</CustomerLabel>
                                            <CustomerValue>
                                                {parsedCustomer.phoneNumber}
                                            </CustomerValue>
                                        </CustomerFieldGroup>
                                    )}
                                    {(parsedCustomer.address ||
                                        parsedCustomer.postalCode ||
                                        parsedCustomer.city) && (
                                        <CustomerFieldGroup>
                                            <CustomerLabel>Address</CustomerLabel>
                                            <CustomerValue>
                                                {[
                                                    parsedCustomer.address,
                                                    [parsedCustomer.postalCode, parsedCustomer.city]
                                                        .filter(Boolean)
                                                        .join(' '),
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </CustomerValue>
                                        </CustomerFieldGroup>
                                    )}
                                </CustomerGrid>
                            </CustomerPreview>
                        )}
                        <PreviewTable>
                            <thead>
                                <tr>
                                    <th>Article</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Unit Price</th>
                                    <th>VAT %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedRows.map((row, i) => (
                                    <tr key={row.rowId ?? i}>
                                        <td>{row.articleNumber}</td>
                                        <td>{row.description}</td>
                                        <td>{row.quantity}</td>
                                        <td>{row.unitPrice}</td>
                                        <td>{row.vatRate}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </PreviewTable>
                    </ResultContainer>
                )}

                {(state === 'idle' || state === 'hasContent' || state === 'error') && (
                    <FocusLock>
                        <Description>
                            Paste text, images, or drop files (images, PDFs) containing product
                            information. The AI will extract line items and customer information to
                            prefill the form.
                        </Description>

                        <DropZone
                            onPaste={handlePaste}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            $isDragOver={isDragOver}
                            $hasContent={hasContent}
                            data-testid="drop-zone"
                        >
                            <StyledTextArea
                                ref={textAreaRef}
                                placeholder="Paste or type content here..."
                                onChange={handleTextChange}
                                data-testid="magic-input-textarea"
                            />

                            {content.previews.length > 0 && (
                                <PreviewArea data-testid="preview-area">
                                    {content.previews.map((preview, i) => (
                                        <PreviewItem key={i}>
                                            {preview.type === 'image' && preview.url && (
                                                <PreviewImage
                                                    src={preview.url}
                                                    alt={preview.name}
                                                />
                                            )}
                                            {preview.type === 'pdf' && <PdfBadge>PDF</PdfBadge>}
                                            <PreviewName>{preview.name}</PreviewName>
                                        </PreviewItem>
                                    ))}
                                </PreviewArea>
                            )}

                            {!hasContent && (
                                <DropHint>
                                    Drop files here or{' '}
                                    <BrowseLink
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        browse
                                    </BrowseLink>
                                </DropHint>
                            )}
                        </DropZone>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                            multiple
                            onChange={handleFileSelect}
                            hidden
                            data-testid="file-input"
                        />

                        {fileSizeWarning && (
                            <FileSizeWarning data-testid="file-size-warning">
                                {fileSizeWarning}
                            </FileSizeWarning>
                        )}

                        {hasContent && (
                            <ActionRow>
                                <BrowseButton
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={!!fileSizeWarning}
                                >
                                    Add files
                                </BrowseButton>
                                <ClearButton type="button" onClick={handleClear}>
                                    Clear
                                </ClearButton>
                            </ActionRow>
                        )}
                    </FocusLock>
                )}

                {state === 'error' && errorMessage && (
                    <ErrorText data-testid="error-message">{errorMessage}</ErrorText>
                )}
            </ModalContainer>

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    <Button onClick={hide} large>
                        Cancel
                    </Button>

                    {state === 'done' ? (
                        <Button
                            onClick={handleConfirmImport}
                            primary
                            large
                            data-testid="confirm-import"
                        >
                            Use these items
                        </Button>
                    ) : (
                        <Button
                            onClick={handleImport}
                            primary
                            large
                            disabled={!hasContent || state === 'processing' || !!fileSizeWarning}
                            data-testid="import-button"
                        >
                            Import
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

export default withTheme(MagicPaylinkModal) as React.ComponentType<
    Omit<MagicPaylinkModalProps, 'theme'>
>;

const shimmer = keyframes`
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
`;

const AiHeaderTitle = styled.span`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-family: ${props => props.theme.text.font.medium};
    font-size: ${props => props.theme.text.size.large};
    color: ${props => props.theme.colors.text.primary};
`;

const SparkleIcon = styled.span`
    display: inline-block;
    font-size: 1.4em;
    background: linear-gradient(
        90deg,
        ${props => props.theme.colors.primary} 0%,
        ${props => props.theme.colors.accent3} 50%,
        ${props => props.theme.colors.primary} 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 3s linear infinite;
`;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 4rem 0;
`;

const Description = styled.p`
    color: ${props => props.theme.colors.text.secondary};
    margin-bottom: 1.5rem;
    font-size: ${props => props.theme.text.size.medium};
`;

const FileSizeWarning = styled.p`
    color: ${props => props.theme.colors.error};
    font-size: ${props => props.theme.text.size.small};
    margin-top: 0.5rem;
`;

const DropZone = styled.div<{ $isDragOver: boolean; $hasContent: boolean }>`
    border: 0.2rem dashed
        ${props => (props.$isDragOver ? props.theme.colors.primary : props.theme.colors.border)};
    border-radius: 0.8rem;
    padding: 1.5rem;
    min-height: 16rem;
    background: ${props =>
        props.$isDragOver ? props.theme.colors.subtle.regular : props.theme.colors.light};
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
`;

const StyledTextArea = styled.textarea`
    width: 100%;
    min-height: 8rem;
    border: none;
    background: transparent;
    resize: vertical;
    font-family: inherit;
    font-size: ${props => props.theme.text.size.medium};
    color: ${props => props.theme.colors.text.primary};
    outline: none;

    &::placeholder {
        color: ${props => props.theme.colors.text.secondary};
    }
`;

const PreviewArea = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 0.1rem solid ${props => props.theme.colors.border};
`;

const PreviewItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    max-width: 8rem;
`;

const PreviewImage = styled.img`
    width: 6rem;
    height: 6rem;
    object-fit: cover;
    border-radius: 0.4rem;
    border: 0.1rem solid ${props => props.theme.colors.border};
`;

const PdfBadge = styled.div`
    width: 6rem;
    height: 6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.theme.colors.subtle.regular};
    border-radius: 0.4rem;
    font-family: ${props => props.theme.text.font.bold};
    font-size: ${props => props.theme.text.size.medium};
    color: ${props => props.theme.colors.text.secondary};
`;

const PreviewName = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 8rem;
`;

const DropHint = styled.div`
    text-align: center;
    color: ${props => props.theme.colors.text.secondary};
    margin-top: auto;
    padding: 1rem 0;
`;

const BrowseLink = styled.button`
    background: none;
    border: none;
    color: ${props => props.theme.colors.primary};
    cursor: pointer;
    text-decoration: underline;
    font-size: inherit;
    font-family: inherit;
    padding: 0;
`;

const ActionRow = styled.div`
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
`;

const BrowseButton = styled.button`
    background: none;
    border: 0.1rem solid ${props => props.theme.colors.border};
    border-radius: 0.4rem;
    padding: 0.4rem 1rem;
    cursor: pointer;
    color: ${props => props.theme.colors.text.primary};
    font-family: ${props => props.theme.text.font.medium};
    font-size: ${props => props.theme.text.size.small};

    &:hover:not(:disabled) {
        background: ${props => props.theme.colors.subtle.regular};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ClearButton = styled(BrowseButton)`
    color: ${props => props.theme.colors.text.primary};
`;

const ErrorText = styled.p`
    color: ${props => props.theme.colors.error};
    margin-top: 1rem;
    font-size: ${props => props.theme.text.size.medium};
`;

const ResultContainer = styled.div`
    padding: 1rem 0;
`;

const CustomerPreview = styled.div`
    background: ${props => props.theme.colors.light};
    border: 0.1rem solid ${props => props.theme.colors.border};
    border-left: 0.3rem solid ${props => props.theme.colors.primary};
    border-radius: 0.4rem;
    padding: 1.2rem 1.5rem;
    margin-bottom: 1.5rem;
`;

const CustomerPreviewTitle = styled.div`
    font-family: ${props => props.theme.text.font.medium};
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.text.size.medium};
    margin-bottom: 0.8rem;
`;

const CustomerGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem 2rem;
`;

const CustomerFieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
`;

const CustomerLabel = styled.span`
    font-size: ${props => props.theme.text.size.small};
    color: ${props => props.theme.colors.text.secondary};
    font-family: ${props => props.theme.text.font.medium};
`;

const CustomerValue = styled.span`
    font-size: ${props => props.theme.text.size.medium};
    color: ${props => props.theme.colors.text.primary};
`;

const SuccessText = styled.p`
    color: ${props => props.theme.colors.text.primary};
    font-family: ${props => props.theme.text.font.medium};
    margin-bottom: 1.5rem;
`;

const PreviewTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        text-align: left;
        padding: 0.6rem 1rem;
        border-bottom: 0.1rem solid ${props => props.theme.colors.border};
        font-size: ${props => props.theme.text.size.medium};
    }

    th {
        font-family: ${props => props.theme.text.font.medium};
        color: ${props => props.theme.colors.text.secondary};
    }

    td {
        color: ${props => props.theme.colors.text.primary};
    }
`;
